import { motion } from "framer-motion";

import {
  ArrowRight,
  CheckCircle2,
  MessageCircle,
} from "lucide-react";

import heroImg from "../../../../Assets/Images/whatsapp/whatsapp-hero.png";
import { useState } from "react";
import ContactModal from "../../../../Assets/Common/ContactModal";

// ============================================================
// ANIMATION
// ============================================================

const fadeUp = {
  hidden: {
    opacity: 0,
    y: 40,
  },

  visible: (i = 1) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.15,
      duration: 0.7,
      ease: "easeOut",
    },
  }),
};

// ============================================================
// HERO SECTION
// ============================================================

export default function HeroSection() {
  const [openModal, setOpenModal] = useState(false);

  return (
    <section
      className="
        relative
        overflow-hidden
        py-8
        pt-14
        font-saira
        bg-[var(--bg-primary)]
        transition-colors
        duration-500

        min-[375px]:py-9
        min-[375px]:pt-16

        sm:py-11
        sm:pt-[76px]

        md:py-14
        md:pt-24

        lg:py-20
        lg:pt-32
      "
    >
      {/* ========================================================
          BACKGROUND GLOW
          KEEP WHATSAPP GREEN IN BOTH THEMES
      ======================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          left-1/2
          top-0
          h-[250px]
          w-[250px]
          -translate-x-1/2
          rounded-full
          bg-green-500/10
          blur-[100px]

          min-[375px]:h-[300px]
          min-[375px]:w-[300px]

          sm:h-[360px]
          sm:w-[360px]
          sm:blur-[125px]

          md:h-[430px]
          md:w-[430px]
          md:blur-[145px]

          lg:h-[500px]
          lg:w-[500px]
          lg:blur-[180px]
        "
      />

      {/* ========================================================
          SECONDARY GREEN GLOW
          KEEP WHATSAPP GREEN IN BOTH THEMES
      ======================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          right-[-120px]
          top-[35%]
          h-[220px]
          w-[220px]
          rounded-full
          bg-green-500/5
          blur-[100px]

          sm:right-[-100px]
          sm:h-[280px]
          sm:w-[280px]

          lg:right-[-80px]
          lg:h-[360px]
          lg:w-[360px]
        "
      />

      {/* ========================================================
          MAIN CONTAINER
      ======================================================== */}

      <div
        className="
          relative
          z-10
          mx-auto
          w-full
          max-w-7xl
          px-4

          min-[375px]:px-5
          sm:px-6
          md:px-7
          lg:px-8
        "
      >
        {/* ======================================================
            MAIN GRID
        ====================================================== */}

        <div
          className="
            grid
            grid-cols-1
            items-center
            gap-9

            min-[375px]:gap-10

            sm:gap-12

            md:gap-14

            lg:grid-cols-2
            lg:gap-14
          "
        >
          {/* ====================================================
              LEFT CONTENT
          ==================================================== */}

          <div className="min-w-0">
            {/* ==================================================
                BADGE
                GREEN — UNCHANGED IN BOTH THEMES
            ================================================== */}

            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{
                once: true,
                amount: 0.2,
              }}
              className="
                inline-flex
                max-w-full
                items-center
                gap-1.5
                rounded-full
                border
                border-green-500/30
                bg-green-500/10
                px-3
                py-1.5
                text-[10px]
                font-medium
                leading-5
                text-green-400
                backdrop-blur-sm

                min-[375px]:gap-2
                min-[375px]:px-3.5
                min-[375px]:py-1.5
                min-[375px]:text-[11px]

                sm:px-4
                sm:py-2
                sm:text-xs

                md:text-sm
              "
            >
              <MessageCircle
                className="
                  h-3.5
                  w-3.5
                  shrink-0

                  min-[375px]:h-4
                  min-[375px]:w-4

                  sm:h-[17px]
                  sm:w-[17px]

                  md:h-[18px]
                  md:w-[18px]
                "
              />

              <span className="truncate">
                Official WhatsApp Business Solutions
              </span>
            </motion.div>

            {/* ==================================================
                HEADING
                THEME AWARE
            ================================================== */}

            <motion.h1
              custom={1}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{
                once: true,
                amount: 0.2,
              }}
              className="
                mt-5
                max-w-3xl
                text-[29px]
                font-bold
                leading-[1.1]
                tracking-tight
                text-[var(--text-primary)]

                min-[375px]:mt-5
                min-[375px]:text-[31px]

                sm:mt-6
                sm:text-[36px]
                sm:leading-[1.1]

                md:text-[42px]

                lg:mt-6
                lg:text-5xl
                lg:leading-tight
              "
            >
              Transform Customer Conversations with{" "}
              <span className="text-green-400">
                WhatsApp Business API
              </span>
            </motion.h1>

            {/* ==================================================
                DESCRIPTION
                THEME AWARE
            ================================================== */}

            <motion.p
              custom={2}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{
                once: true,
                amount: 0.2,
              }}
              className="
                mt-5
                max-w-2xl
                text-[14px]
                leading-6
                text-[var(--text-muted)]

                min-[375px]:mt-5
                min-[375px]:text-[15px]
                min-[375px]:leading-7

                sm:mt-6
                sm:text-base
                sm:leading-7

                md:mt-7
                md:text-lg
                md:leading-8

                lg:mt-8
                lg:text-lg
                lg:leading-8
              "
            >
              Build meaningful customer relationships using the
              official WhatsApp Business API. Automate
              conversations, empower your support teams, launch
              marketing campaigns, and deliver secure customer
              experiences from one intelligent communication
              platform.
            </motion.p>

            {/* ==================================================
                CTA BUTTON
                GREEN — UNCHANGED
            ================================================== */}

            <motion.div
              custom={3}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{
                once: true,
                amount: 0.2,
              }}
              className="
                mt-6
                flex
                w-full

                min-[375px]:mt-7

                sm:mt-8
                sm:flex-row

                lg:mt-10
              "
            >
              <motion.button
                type="button"
                whileHover={{
                  y: -2,
                  scale: 1.01,
                }}
                whileTap={{
                  scale: 0.97,
                }}
                transition={{
                  duration: 0.2,
                }}
                onClick={() => {
                  setOpenModal(true);
                }}
                className="
                  group
                  inline-flex
                  w-full
                  items-center
                  justify-center
                  gap-2
                  rounded-xl
                  bg-green-500
                  px-5
                  py-3.5
                  text-sm
                  font-semibold
                  text-black
                  shadow-[0_10px_35px_rgba(34,197,94,0.18)]
                  transition-all
                  duration-300
                  hover:bg-green-600
                  hover:shadow-[0_15px_40px_rgba(34,197,94,0.25)]

                  min-[375px]:px-6
                  min-[375px]:py-3.5

                  sm:w-auto
                  sm:px-7
                  sm:py-4
                  sm:text-base

                  lg:px-7
                  lg:py-4
                "
              >
                <span>Book Free Consultation</span>

                <ArrowRight
                  className="
                    h-4
                    w-4
                    shrink-0
                    transition-transform
                    duration-300
                    group-hover:translate-x-1

                    sm:h-[18px]
                    sm:w-[18px]
                  "
                />
              </motion.button>
            </motion.div>

            {/* ==================================================
                TRUST INDICATORS
                TEXT → THEME AWARE
            ================================================== */}

            <motion.div
              custom={4}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{
                once: true,
                amount: 0.2,
              }}
              className="
                mt-7
                grid
                grid-cols-1
                gap-2.5
                text-xs
                text-[var(--text-muted)]

                min-[375px]:mt-8
                min-[375px]:gap-3

                sm:mt-9
                sm:grid-cols-2
                sm:gap-x-6
                sm:gap-y-3
                sm:text-sm

                md:mt-10
                md:grid-cols-3
                md:gap-4

                lg:mt-12
                lg:flex
                lg:flex-wrap
                lg:gap-8
                lg:text-sm
              "
            >
              {/* Trust 1 */}

              <div className="flex min-w-0 items-center gap-2">
                <CheckCircle2
                  className="
                    h-4
                    w-4
                    shrink-0
                    text-green-400

                    sm:h-[18px]
                    sm:w-[18px]
                  "
                />

                <span className="leading-5">
                  Official WhatsApp API
                </span>
              </div>

              {/* Trust 2 */}

              <div className="flex min-w-0 items-center gap-2">
                <CheckCircle2
                  className="
                    h-4
                    w-4
                    shrink-0
                    text-green-400

                    sm:h-[18px]
                    sm:w-[18px]
                  "
                />

                <span className="leading-5">
                  Enterprise Security
                </span>
              </div>

              {/* Trust 3 */}

              <div className="flex min-w-0 items-center gap-2">
                <CheckCircle2
                  className="
                    h-4
                    w-4
                    shrink-0
                    text-green-400

                    sm:h-[18px]
                    sm:w-[18px]
                  "
                />

                <span className="leading-5">
                  99.9% Uptime
                </span>
              </div>
            </motion.div>
          </div>

          {/* ====================================================
              RIGHT IMAGE
          ==================================================== */}

          <motion.div
            initial={{
              opacity: 0,
              scale: 0.94,
            }}
            whileInView={{
              opacity: 1,
              scale: 1,
            }}
            viewport={{
              once: true,
              amount: 0.15,
            }}
            transition={{
              duration: 0.8,
              ease: "easeOut",
            }}
            animate={{
              y: [0, -8, 0],
            }}
            className="
              relative
              mx-auto
              w-full
              min-w-0
              max-w-[650px]

              lg:max-w-none
            "
          >
            {/* ==================================================
                IMAGE CONTAINER
            ================================================== */}

            <div
              className="
                relative
                w-full
                overflow-hidden
                rounded-2xl
                border
                border-[var(--border-primary)]
                bg-[var(--bg-secondary)]
                shadow-[0_0_45px_rgba(37,211,102,0.12)]

                sm:rounded-3xl

                lg:h-[500px]
                lg:rounded-3xl
                lg:shadow-[0_0_70px_rgba(37,211,102,0.2)]
              "
            >
              <img
                src={heroImg}
                alt="WhatsApp Business API Dashboard"
                loading="eager"
                decoding="async"
                className="
                  block
                  h-auto
                  w-full
                  max-w-full
                  object-contain

                  lg:h-[500px]
                  lg:w-full
                  lg:object-fill
                "
              />

              {/* ==================================================
                  IMAGE OVERLAY
              ================================================== */}

              <div
                className="
                  pointer-events-none
                  absolute
                  inset-0
                  bg-gradient-to-t
                  from-black/20
                  via-transparent
                  to-transparent
                "
              />

              {/* ==================================================
                  GREEN AMBIENT GLOW
              ================================================== */}

              <div
                className="
                  pointer-events-none
                  absolute
                  bottom-[-30%]
                  left-1/2
                  h-[45%]
                  w-[70%]
                  -translate-x-1/2
                  rounded-full
                  bg-green-500/10
                  blur-[70px]
                "
              />
            </div>

            {/* ==================================================
                FLOATING CARD
            ================================================== */}

            <motion.div
              initial={{
                opacity: 0,
                y: 15,
              }}
              whileInView={{
                opacity: 1,
                y: 0,
              }}
              viewport={{
                once: true,
                amount: 0.2,
              }}
              transition={{
                delay: 0.5,
                duration: 0.5,
                ease: "easeOut",
              }}
              className="
                relative
                ml-auto
                mr-auto
                w-fit
                max-w-[calc(100%-24px)]
                rounded-lg
                border
                border-[var(--border-primary)]
                bg-[var(--card-bg)]
                px-3
                py-2
                shadow-[0_8px_25px_rgba(0,0,0,0.35)]
                backdrop-blur-xl

                min-[375px]:mt-3
                min-[375px]:px-3
                min-[375px]:py-2.5

                sm:mt-4
                sm:rounded-xl
                sm:px-4
                sm:py-2.5

                md:px-4
                md:py-3

                lg:absolute
                lg:bottom-0
                lg:left-[-12px]
                lg:right-auto
                lg:mt-0
                lg:ml-0
                lg:mr-0
                lg:w-auto
                lg:max-w-none
                lg:min-w-[320px]
                lg:rounded-2xl
                lg:px-5
                lg:py-4
              "
            >
              {/* ==================================================
                  FLOATING CARD TITLE
              ================================================== */}

              <p
                className="
                  whitespace-nowrap
                  text-[12px]
                  font-semibold
                  leading-4
                  text-green-400

                  min-[375px]:text-[13px]

                  sm:text-sm

                  md:text-base

                  lg:text-base
                "
              >
                End-to-End Automation
              </p>

              {/* ==================================================
                  FLOATING CARD DESCRIPTION
              ================================================== */}

              <p
                className="
                  mt-0.5
                  whitespace-nowrap
                  text-[9px]
                  leading-4
                  text-[var(--text-muted)]

                  min-[375px]:text-[10px]

                  sm:text-[11px]

                  md:text-xs

                  lg:text-sm
                "
              >
                Simplify customer communication.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* ========================================================
          CONTACT MODAL
      ======================================================== */}

      <ContactModal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        initialService="API Services"
        initialSubService="WhatsApp API Services"
      />
    </section>
  );
}